import { AlertTriangle, Info, CheckCircle2, X, XCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

const variantStyles = {
  warning:
    'border-status-accent-orange/30 bg-status-accent-orange/5 [&_[data-slot=banner-icon]]:text-status-accent-orange',
  error: 'border-status-error/30 bg-status-error/5 [&_[data-slot=banner-icon]]:text-status-error',
  info: 'border-status-info/30 bg-status-info/5 [&_[data-slot=banner-icon]]:text-status-info',
  success:
    'border-status-success/30 bg-status-success/5 [&_[data-slot=banner-icon]]:text-status-success',
} as const;

const defaultIcons = {
  warning: AlertTriangle,
  error: XCircle,
  info: Info,
  success: CheckCircle2,
} as const;

type BannerIntent = keyof typeof variantStyles;

interface BannerProps extends React.ComponentPropsWithoutRef<'div'> {
  intent?: BannerIntent;
  icon?: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
  action?: React.ReactNode;
  onDismiss?: () => void;
}

export function Banner({
  intent = 'info',
  icon,
  children,
  action,
  onDismiss,
  className,
  ...rest
}: BannerProps) {
  const Icon = icon ?? defaultIcons[intent];

  return (
    <div
      role="status"
      data-slot="banner"
      className={cn(
        'flex w-full items-start gap-3 rounded-lg border p-3',
        variantStyles[intent],
        className,
      )}
      {...rest}
    >
      <Icon data-slot="banner-icon" className="mt-0.5 h-4 w-4 shrink-0" />
      <div data-slot="banner-body" className="min-w-0 flex-1">
        {children}
      </div>
      {action ? (
        <div data-slot="banner-action" className="shrink-0">
          {action}
        </div>
      ) : null}
      {onDismiss ? (
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 shrink-0 -m-1"
          onClick={onDismiss}
          aria-label="Dismiss"
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      ) : null}
    </div>
  );
}
